import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Search, Plus, Users as UsersIcon, Wallet, CalendarOff, CalendarDays, FileText, Package, Pencil, RotateCcw, UserMinus } from 'lucide-react';
import api from '../utils/api';
import ExportExcelButton from '../components/ExportExcelButton';
import PersonnelModal from '../components/personnel/PersonnelModal';
import PersonnelDetailDrawer from '../components/personnel/PersonnelDetailDrawer';
import TerminateModal from '../components/personnel/TerminateModal';
import { formatRecordDate } from '../utils/dateUtils';
import { STATUS_LABELS, formatAmount } from '../utils/personnelLabels';
import toast from 'react-hot-toast';

const STATUS_FILTERS = [
    { key: 'aktif', label: 'Aktif' },
    { key: 'ayrildi', label: 'Ayrılan' },
    { key: '', label: 'Tümü' },
];

const Personnel = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin';

    const [personnel, setPersonnel] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('aktif');
    const [isLoading, setIsLoading] = useState(true);
    const [summary, setSummary] = useState({ activeCount: 0, unpaidTotals: [], onLeaveToday: 0, unreturnedAssets: 0 });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [personToEdit, setPersonToEdit] = useState(null);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [selectedTab, setSelectedTab] = useState('genel');
    const [terminateTarget, setTerminateTarget] = useState(null);
    const [terminateUnreturned, setTerminateUnreturned] = useState(0);

    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });
    const scrollSentinelRef = useRef(null);

    // Admin dışı erişimi engelle
    useEffect(() => {
        if (user && user.role !== 'admin') {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const fetchSummary = useCallback(async () => {
        try {
            const res = await api.get('/personnel/summary');
            setSummary(res.data);
        } catch (error) {
            // özet kritik değil, sessiz geç
        }
    }, []);

    const fetchPersonnel = useCallback(async (pageNum = 1, append = false) => {
        setIsLoading(true);
        try {
            const res = await api.get('/personnel', {
                params: { search, status: statusFilter, page: pageNum, limit: 20 },
            });
            if (res.data.data) {
                setPersonnel((prev) => append ? [...prev, ...res.data.data] : res.data.data);
                setPagination(res.data.pagination);
            }
        } catch (error) {
            toast.error('Personel listesi yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, [search, statusFilter]);

    const loadAllPersonnelForExport = useCallback(async () => {
        const res = await api.get('/personnel', {
            params: { search, status: statusFilter, page: 1, limit: 100000 },
        });
        return res.data.data || res.data;
    }, [search, statusFilter]);

    useEffect(() => {
        if (!isAdmin) return;
        const delay = setTimeout(() => {
            fetchPersonnel(1, false);
            fetchSummary();
        }, 500);
        return () => clearTimeout(delay);
    }, [fetchPersonnel, fetchSummary, isAdmin]);

    const loadMore = useCallback(() => {
        if (!isLoading && pagination.page < pagination.totalPages) {
            fetchPersonnel(pagination.page + 1, true);
        }
    }, [fetchPersonnel, isLoading, pagination.page, pagination.totalPages]);

    useEffect(() => {
        const sentinel = scrollSentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadMore();
        }, { rootMargin: '200px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMore]);

    const refreshAll = () => {
        fetchPersonnel(1, false);
        fetchSummary();
    };

    const handleEdit = (person) => {
        setPersonToEdit(person);
        setIsModalOpen(true);
    };

    const openPersonnelTab = (person, tab = 'genel') => {
        setSelectedTab(tab);
        setSelectedPerson(person);
    };

    const handleTerminateClick = async (person) => {
        // İade edilmemiş zimmet sayısını hesapla
        let unreturned = 0;
        try {
            const res = await api.get(`/personnel/${person.id}/assets`);
            unreturned = (res.data || []).filter((a) => !a.isReturned).length;
        } catch (error) {
            // sayım başarısızsa 0 ile devam
        }
        setTerminateUnreturned(unreturned);
        setTerminateTarget(person);
    };

    const handleRehire = async (person) => {
        try {
            await api.patch(`/personnel/${person.id}/rehire`);
            toast.success('Personel yeniden aktif olarak işaretlendi.');
            refreshAll();
        } catch (error) {
            toast.error('İşlem başarısız.');
        }
    };

    const excelMapper = (row) => ({
        'Ad Soyad': row.fullName,
        'Görev': row.position || '-',
        'Telefon': row.phone || '-',
        'İşe Giriş': row.hireDate ? formatRecordDate(row.hireDate) : '-',
        'Haftalık Ücret': row.weeklyWage,
        'Para Birimi': row.wageCurrency,
        'Durum': STATUS_LABELS[row.status] || row.status,
        'Ayrılış Tarihi': row.terminationDate ? formatRecordDate(row.terminationDate) : '-',
        'Acil Durum Kişisi': row.emergencyName || '-',
        'Acil Durum Telefonu': row.emergencyPhone || '-',
        'Notlar': row.notes || '-',
    });

    if (!isAdmin) return null;

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-dark-text mb-2 tracking-tight">Personel Kayıtları</h1>
                    <p className="text-dark-muted text-sm md:text-base">Çalışan bilgileri, ücretler, izinler, belgeler ve zimmet takibi.</p>
                </div>
                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                    <ExportExcelButton data={personnel} loadData={loadAllPersonnelForExport} mapper={excelMapper} sheetName="Personel" filename="Personel_Listesi.xlsx" />
                    <button
                        onClick={() => { setPersonToEdit(null); setIsModalOpen(true); }}
                        className="w-full md:w-auto flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-5 py-3 rounded-xl font-bold tracking-wide transition-all shadow-lg shadow-primary-500/20 hover:scale-105 active:scale-95"
                    >
                        <Plus size={20} className="stroke-[3]" />
                        <span>YENİ PERSONEL</span>
                    </button>
                </div>
            </div>

            {/* Özet kartları */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="glass border border-dark-border rounded-xl p-4 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-primary-500/10 text-primary-500"><UsersIcon size={22} /></div>
                    <div>
                        <div className="text-xs text-dark-muted uppercase tracking-wider">Aktif Personel</div>
                        <div className="text-2xl font-bold text-dark-text">{summary.activeCount}</div>
                    </div>
                </div>
                <div className="glass border border-dark-border rounded-xl p-4 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-violet-500/10 text-violet-400"><Package size={22} /></div>
                    <div>
                        <div className="text-xs text-dark-muted uppercase tracking-wider">İade Bekleyen Zimmet</div>
                        <div className="text-2xl font-bold text-dark-text">{summary.unreturnedAssets || 0}</div>
                    </div>
                </div>
                <div className="glass border border-dark-border rounded-xl p-4 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-orange-500/10 text-orange-500"><Wallet size={22} /></div>
                    <div className="min-w-0">
                        <div className="text-xs text-dark-muted uppercase tracking-wider">Ödenmemiş (Bekleyen)</div>
                        {summary.unpaidTotals.length === 0 ? (
                            <div className="text-lg font-bold text-dark-text">Yok</div>
                        ) : (
                            <div className="flex flex-wrap gap-x-3 text-sm font-bold text-dark-text">
                                {summary.unpaidTotals.map((t) => (
                                    <span key={t.currency}>{formatAmount(t.total)} {t.currency}</span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="glass border border-dark-border rounded-xl p-4 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-blue-500/10 text-blue-500"><CalendarOff size={22} /></div>
                    <div>
                        <div className="text-xs text-dark-muted uppercase tracking-wider">Bugün İzinli</div>
                        <div className="text-2xl font-bold text-dark-text">{summary.onLeaveToday}</div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <PersonnelModal
                isOpen={isModalOpen}
                editData={personToEdit}
                onClose={() => { setIsModalOpen(false); setPersonToEdit(null); }}
                onRefresh={() => refreshAll()}
            />

            {/* Arama + filtre */}
            <div className="glass p-4 rounded-xl mb-6 flex flex-col md:flex-row gap-4 border border-dark-border">
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-dark-muted">
                        <Search size={18} />
                    </div>
                    <input
                        type="text"
                        placeholder="Ad, görev veya telefon ara..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-11 pr-4 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg text-dark-text placeholder:text-dark-muted focus:outline-none focus:border-primary-500 transition-colors"
                    />
                </div>
                <div className="flex gap-2">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setStatusFilter(f.key)}
                            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${statusFilter === f.key ? 'bg-primary-500 text-white' : 'bg-dark-bg/50 text-dark-muted hover:text-dark-text border border-dark-border'}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tablo */}
            <div className="glass border border-dark-border rounded-2xl overflow-hidden shadow-2xl">
                <div className="mobile-table-hint lg:hidden">Personel bilgileri ve hızlı işlemler için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-dark-card text-dark-muted border-b border-dark-border uppercase tracking-wider text-[11px] md:text-xs">
                            <tr>
                                <th className="px-3 md:px-6 py-4 font-semibold">Ad Soyad</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden sm:table-cell">Görev</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden md:table-cell">Telefon</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden lg:table-cell">İşe Giriş</th>
                                <th className="px-3 md:px-6 py-4 font-semibold">Haftalık Ücret</th>
                                <th className="px-3 md:px-6 py-4 font-semibold text-center">Durum</th>
                                <th className="px-3 md:px-6 py-4 font-semibold text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border">
                            {isLoading && personnel.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-dark-muted">
                                        <div className="inline-block w-6 h-6 border-2 border-dark-muted border-t-transparent rounded-full animate-spin mb-2"></div>
                                        <p>Yükleniyor...</p>
                                    </td>
                                </tr>
                            ) : personnel.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-dark-muted font-medium">
                                        Eşleşen personel kaydı bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                personnel.map((row) => {
                                    const active = row.status === 'aktif';
                                    return (
                                        <tr key={row.id} className="hover:bg-dark-hover transition-colors cursor-pointer" onClick={() => openPersonnelTab(row)}>
                                            <td className="px-3 md:px-6 py-4">
                                                <div className="font-semibold text-dark-text">{row.fullName}</div>
                                                <div className="text-xs text-dark-muted sm:hidden">{row.position || '-'}</div>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 hidden sm:table-cell text-dark-muted">{row.position || '-'}</td>
                                            <td className="px-3 md:px-6 py-4 hidden md:table-cell text-dark-muted">{row.phone || '-'}</td>
                                            <td className="px-3 md:px-6 py-4 hidden lg:table-cell text-dark-muted">{row.hireDate ? formatRecordDate(row.hireDate) : '-'}</td>
                                            <td className="px-3 md:px-6 py-4">
                                                <span className="font-bold text-dark-text">{formatAmount(row.weeklyWage)}</span> <span className="text-xs text-dark-muted">{row.wageCurrency}</span>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 text-center">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${active ? 'bg-green-500/15 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                                                    {STATUS_LABELS[row.status] || row.status}
                                                </span>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1">
                                                    {active && (
                                                        <>
                                                            <button onClick={() => openPersonnelTab(row, 'odemeler')} className="p-1.5 text-dark-muted hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors" title="Ödeme kaydet">
                                                                <Wallet size={18} />
                                                            </button>
                                                            <button onClick={() => openPersonnelTab(row, 'izinler')} className="p-1.5 text-dark-muted hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors" title="İzin ekle">
                                                                <CalendarDays size={18} />
                                                            </button>
                                                            <button onClick={() => openPersonnelTab(row, 'belgeler')} className="hidden lg:inline-flex p-1.5 text-dark-muted hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors" title="Belge ekle">
                                                                <FileText size={18} />
                                                            </button>
                                                            <button onClick={() => openPersonnelTab(row, 'zimmet')} className="hidden lg:inline-flex p-1.5 text-dark-muted hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors" title="Zimmet ver">
                                                                <Package size={18} />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button onClick={() => handleEdit(row)} className="p-1.5 text-dark-muted hover:text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors" title="Düzenle">
                                                        <Pencil size={18} />
                                                    </button>
                                                    {active ? (
                                                        <button onClick={() => handleTerminateClick(row)} className="p-1.5 text-dark-muted hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors" title="İşten Çıkar">
                                                            <UserMinus size={18} />
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => handleRehire(row)} className="p-1.5 text-dark-muted hover:text-green-500 hover:bg-green-500/10 rounded-lg transition-colors" title="İşe Geri Al">
                                                            <RotateCcw size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination.page < pagination.totalPages && (
                    <div className="flex items-center justify-center p-6 border-t border-dark-border bg-dark-bg/30">
                        {isLoading ? (
                            <div className="flex items-center gap-3 text-dark-muted font-medium">
                                <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                                <span>Daha fazla yükleniyor...</span>
                            </div>
                        ) : (
                            <button onClick={() => fetchPersonnel(pagination.page + 1, true)} className="text-sm font-semibold text-primary-500 hover:text-primary-600 px-6 py-2 rounded-lg bg-primary-500/10 hover:bg-primary-500/20 transition-colors">
                                Daha Fazla Yükle ({pagination.total - personnel.length} kayıt kaldı)
                            </button>
                        )}
                    </div>
                )}
                <div ref={scrollSentinelRef} className="h-1" />
            </div>

            {/* Detay drawer */}
            {selectedPerson && (
                <PersonnelDetailDrawer
                    personnel={selectedPerson}
                    initialTab={selectedTab}
                    onClose={() => setSelectedPerson(null)}
                    onEdit={(p) => { setSelectedPerson(null); handleEdit(p); }}
                    onTerminate={(p) => { setSelectedPerson(null); handleTerminateClick(p); }}
                    onRefresh={refreshAll}
                />
            )}

            {/* İşten çıkar */}
            <TerminateModal
                isOpen={!!terminateTarget}
                personnel={terminateTarget}
                unreturnedCount={terminateUnreturned}
                onClose={() => setTerminateTarget(null)}
                onDone={refreshAll}
            />
        </Layout>
    );
};

export default Personnel;
